// fetch-mattermost-bot-token-from-server.ts
//
// Lấy bot token @avatar tự động từ Edge Function `get-bot-token` (Supabase) khi
// user đã login NAL (id_token còn hạn) — tránh phải hỏi/keychain thủ công cho bot
// mặc định "avatar". Token @avatar nằm ở Supabase secret, KHÔNG bao giờ hardcode
// ở CLI; hàm này chỉ gửi id_token, server tự resolve + trả token.
//
// Fail-soft theo thiết kế: MỌI lỗi (chưa login, network, non-200) → return null,
// KHÔNG throw. Caller (run-comm-setup-phase.ts / comm-connect.ts) fallback
// xuống bước tiếp theo (hỏi tay) khi nhận null.
import { getValidIdToken } from "./user-config-store.js";
import { assertAllowedEndpointHost } from "./validate-endpoint-host-allowlist.js";

// Endpoint cố định — get-bot-token chỉ deploy 1 nơi (không có env override như
// get-pack/auth-device vì đây không cần switch dev/prod theo workspace).
const GET_BOT_TOKEN_ENDPOINT =
  "https://qpdbkewtpkkbcrptnlos.supabase.co/functions/v1/get-bot-token";
const FETCH_TIMEOUT_MS = 10_000; // ~10s theo yêu cầu — get-bot-token chỉ verify token + đọc secret, nhanh.

// Cùng Supabase project với get-pack/auth-device — exact host (không trust
// subdomain *.supabase.co lạ, tránh MITM qua project Supabase của attacker).
const ALLOWED_GET_BOT_TOKEN_HOSTS = {
  exactHosts: ["qpdbkewtpkkbcrptnlos.supabase.co"],
};

interface GetBotTokenResponse {
  botName: string;
  botToken: string;
  requested_by: string;
}

// Validate endpoint qua host allowlist (mirror pattern getPackEndpoint/
// resolveAuthDeviceEndpoint) — dù endpoint ở đây là const cố định (không đọc từ
// env/settings), vẫn gate để nhất quán + phòng trường hợp const bị sửa sai sau này.
function resolveGetBotTokenEndpoint(): string {
  const parsed = new URL(GET_BOT_TOKEN_ENDPOINT);
  assertAllowedEndpointHost(
    parsed.hostname,
    ALLOWED_GET_BOT_TOKEN_HOSTS,
    (message) => new Error(`get-bot-token endpoint ${message}`),
  );
  return GET_BOT_TOKEN_ENDPOINT;
}

// Lấy bot token từ server cho botName chỉ định. Trả null (KHÔNG throw) ở MỌI
// nhánh lỗi — chưa login, network fail, timeout, hoặc server từ chối (401/403/404
// /5xx) — để caller fallback êm xuống hỏi tay.
export async function fetchBotTokenFromServer(botName: string): Promise<string | null> {
  let idToken: string;
  try {
    idToken = await getValidIdToken();
  } catch {
    // Chưa login (NoValidTokenError) hoặc refresh thất bại → không có token để gửi.
    return null;
  }

  let endpoint: string;
  try {
    endpoint = resolveGetBotTokenEndpoint();
  } catch {
    return null;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ botName }),
      signal: controller.signal,
    });
  } catch {
    // Network error / timeout / DNS — fallback êm, không throw.
    return null;
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    // 401 (id_token sai/hết hạn), 403 (hd domain không hợp lệ), 404 (bot không có
    // secret), 5xx (server lỗi) — tất cả fallback êm, KHÔNG throw.
    return null;
  }

  try {
    const data = (await res.json()) as GetBotTokenResponse;
    return typeof data.botToken === "string" && data.botToken.length > 0 ? data.botToken : null;
  } catch {
    // Response không parse được JSON — coi như fail-soft.
    return null;
  }
}
